import type { TiptapDoc } from '@odo/shared-ui';
import {
    emptyRichText,
    isRichTextEmpty,
    serializeRichText,
    toRichTextDoc
} from '../utils/richText';
import type { FaqItemData } from '../providers/faqProvider';
import type { HelpDocumentData } from '../providers/helpCenterProvider';
import type { VideoItemData } from '../providers/videosProvider';
import { DEFAULT_AUDIENCE, type ContentAudience } from '../utils/audience';

/**
 * The three authorable content types, singular so the labels read naturally in
 * buttons and headings. Distinct from `ResourceKind` in the config (plural, and
 * keyed to endpoint paths) and from `ResourceEntryKind` in the search index.
 */
export type AuthoringKind = 'faq' | 'helpDocument' | 'video';

export const AUTHORING_LABEL: Record<AuthoringKind, string> = {
    faq: 'question',
    helpDocument: 'help document',
    video: 'video'
};

/**
 * One editable shape for all three types instead of a union: the form is a single
 * component, and a flat draft keeps the field-by-field wiring readable. Fields
 * outside the edited kind are simply unused; `toPayload` reads only the ones
 * that kind's endpoint accepts.
 */
export interface ResourceDraft {
    kind: AuthoringKind;
    /** Null while creating; the row id when editing. */
    id: number | null;
    question: string;
    /** Rich text: stored as a Tiptap document, JSON-encoded into the column. */
    answer: TiptapDoc;
    title: string;
    slug: string;
    /**
     * True once the slug has been edited by hand. Until then it tracks the
     * title, which is what makes creating a video or guide a two-field job.
     * Never auto-tracks on an existing row: a published slug is a URL.
     */
    slugTouched: boolean;
    /** Rich text, same storage as `answer`. */
    description: TiptapDoc;
    videoUrl: string;
    /** Existing PDF on a guide being edited. */
    pdfFileId: string | null;
    /** A newly picked PDF, uploaded on save and replacing `pdfFileId`. */
    pdfFile: File | null;
    category: string[];
    audience: ContentAudience;
}

const EMPTY_DRAFT: Omit<ResourceDraft, 'kind'> = {
    id: null,
    question: '',
    answer: emptyRichText(),
    title: '',
    slug: '',
    slugTouched: false,
    description: emptyRichText(),
    videoUrl: '',
    pdfFileId: null,
    pdfFile: null,
    category: [],
    audience: DEFAULT_AUDIENCE
};

export function createDraft(kind: AuthoringKind): ResourceDraft {
    return { ...EMPTY_DRAFT, kind };
}

export type AuthorableItem = FaqItemData | HelpDocumentData | VideoItemData;

/** Loads an existing row into a draft. The caller pairs the kind with its item. */
export function draftFromItem(kind: AuthoringKind, item: AuthorableItem): ResourceDraft {
    const draft = {
        ...EMPTY_DRAFT,
        kind,
        id: item.id,
        category: [...item.category],
        audience: item.audience
    };

    if (kind === 'faq') {
        const faq = item as FaqItemData;
        return { ...draft, question: faq.question, answer: toRichTextDoc(faq.answer) };
    }
    if (kind === 'helpDocument') {
        const doc = item as HelpDocumentData;
        return { ...draft, title: doc.title, slug: doc.slug, pdfFileId: doc.pdfFileId };
    }
    const video = item as VideoItemData;
    return {
        ...draft,
        title: video.title,
        slug: video.slug,
        description: toRichTextDoc(video.description),
        videoUrl: video.videoUrl
    };
}

/** Mirrors the server's slug rule: lowercase alphanumeric words joined by hyphens. */
export const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export function slugify(value: string): string {
    return value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 200);
}

/**
 * Client-side mirror of the endpoint validators, under
 * backend/src/routes/api/v2/content. Keeps a missing title or a bad slug out of
 * a round-trip; the server stays the authority.
 */
export function validateDraft(draft: ResourceDraft): string | null {
    if (draft.kind === 'faq') {
        if (!draft.question.trim()) return 'A question is required.';
        if (isRichTextEmpty(draft.answer)) return 'An answer is required.';
        return null;
    }

    if (!draft.title.trim()) return 'A title is required.';
    if (!SLUG_PATTERN.test(draft.slug)) {
        return 'The slug must be lowercase words joined by hyphens, e.g. bonding-protocol.';
    }

    if (draft.kind === 'video') {
        if (!draft.videoUrl.trim()) return 'A video URL is required.';
        try {
            new URL(draft.videoUrl);
        } catch {
            return 'The video URL is not a valid URL.';
        }
        return null;
    }

    // A guide is its PDF: there is nothing to open without one. On edit the
    // existing file stands in for a new pick.
    if (!draft.pdfFile && !draft.pdfFileId) return 'A PDF is required.';
    return null;
}

/**
 * The request body for create and update. Update endpoints accept partials, but
 * every field the form owns is sent either way: a cleared description means
 * "cleared", not "unchanged".
 */
export function toPayload(draft: ResourceDraft, pdfFileId: string | null): Record<string, unknown> {
    const { category, audience } = draft;

    if (draft.kind === 'faq') {
        return {
            question: draft.question.trim(),
            answer: serializeRichText(draft.answer),
            category,
            audience
        };
    }
    if (draft.kind === 'video') {
        return {
            title: draft.title.trim(),
            slug: draft.slug,
            // Serialized even when empty: an author who cleared the description
            // means cleared, and the endpoint treats a present field as a write.
            description: isRichTextEmpty(draft.description)
                ? ''
                : serializeRichText(draft.description),
            videoUrl: draft.videoUrl.trim(),
            category,
            audience
        };
    }
    return {
        title: draft.title.trim(),
        slug: draft.slug,
        // Non-null by the time this runs: validateDraft rejects a guide with no
        // file, and a guide being edited keeps its existing one.
        pdfFileId: pdfFileId ?? draft.pdfFileId,
        category,
        audience
    };
}
